import React, {
    useContext, useEffect, useRef, useState 
} from 'react';
import {
    Box, Slide, Typography 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import dataDogLogger from '../../../../lib/datadog';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants 
} from '../../../../figmaTheme';
import {
    useCookies 
} from 'react-cookie';

type Props = {
    position: number,
    backJump?: number
};

const SUBTITLES = [
    'Try to be as realistic as possible with your answers. This is a <span style="color:#B05DF1;font-weight:700">safe place</span> and Sunnyside is entirely <span style="color:#B05DF1;font-weight:700">judgment free.</span>',
    'We\'ll step through each day together to get a sense of your current baseline, which we\'ll use as the starting point for your plan.',
    ''
];

export const DrinkBaselineIntro = ({
    position,
    backJump = 1
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const [cookies] = useCookies();
    const [startTime, setStartTime] = useState(formContext.startTime);
    const useStyles = makeStyles(() => ({
        content: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        },
        title: {
            marginBottom: '16px',
            transition: 'opacity 0.5s ease-in-out 5s'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        },
        '@keyframes fadeOut': {
            '0%': {
                opacity: '1' 
            },
            '100%': {
                opacity: '0' 
            },
        },
        '@keyframes shrink': {
            '0%': {
                width: '100%',
                height: 'auto',
                borderRadius: 'none' 
            },
            '100%': {
                width: '97px',
                height: '73px',
                borderRadius: '6px' 
            },
        },
        '@keyframes changeColor': {
            '0%': {
                backgroundColor: '#F5F5F5',
                border: 'none' 
            },
            '100%': {
                backgroundColor: '#F7EFFE',
                border: '2px solid #B05DF1' 
            },
        },
        '@keyframes move': {
            '0%': {
                transform: 'none' 
            },
            '100%': {
                transform: 'translate(0, 58px)' 
            },
        },
    }));
    const classes = useStyles();
    const [step, setStep] = useState(formContext.screenDirection === 'left' ? 0 : 1);
    const [subtitle, setSubtitle] = useState(SUBTITLES[step]);
    const [slide, setSlide] = useState(true);
    const [direction, setDirection] = useState(formContext.screenDirection);
    const timeout = useRef(null);

    const onClick = () => {
        if (step === 1) {
            formContext.goNext();
            return;
        }
        setStartTime(new Date().getTime());
        setStep(step + 1);
        setSlide(false);
        setDirection('right');
    };

    const onBackClick = () => {
        if (step > 0) {
            setStartTime(null);
            setStep(step - 1);
            setSlide(false);
            setDirection('left');
        } else formContext.goBack(backJump);

        clearTimeout(timeout.current);
    };

    useEffect(() => {
        if (slide) return;

        setTimeout(() => {
            setSubtitle(SUBTITLES[step]);
        }, 500);
    }, [slide]);

    useEffect(() => {
        setDirection(direction === 'right' ? 'left' : 'right');
        setSlide(true);
    }, [subtitle]);

    useEffect(() => {
        if (typeof step !== 'number') return;
        formContext.fireTrackingEvents(formContext, step === 0 ? 'drink baselines prep 1' : 'drink baselines prep 2', cookies);
    }, [step]);

    useEffect(() => {
        if (!startTime) return;

        const endTime = new Date().getTime();
        const diff = endTime - startTime;

        dataDogLogger(
            'info',
            `Diagnostic screen drink baselines prep ${step + 1} rendered in ${diff}ms`,
            {
                frontendMetric: diff,
                url: `drink baselines prep ${step + 1}`,
                rawData: {
                    formId: formContext.formId
                }
            }
        );
    }, [startTime]);

    return (
        <CardContainer
            sectionKey={'habits'}
            goBack={onBackClick}
            goNext={step <= 1 && onClick}
        >
            <Card>
                <ProgressBar
                    currentPosition={position + step}
                    sectionKey={'habits'}
                />
                <Slide
                    in={true}
                    direction={formContext.screenDirection}
                    timeout={500}
                    mountOnEnter
                    unmountOnExit
                >
                    <Box className={classes.content}>
                        <Typography
                            variant={TypographyVariants.H3}
                            className={classes.title}
                            dangerouslySetInnerHTML={{
                                __html: 'Take us through a <span style="font-weight:700;color:#B05DF1;">typical week of drinking</span> for you.' 
                            }}
                        />
                        <Slide
                            in={slide}
                            direction={direction}
                            timeout={250}
                            mountOnEnter
                        >
                            <Typography
                                variant={TypographyVariants.H6}
                                dangerouslySetInnerHTML={{
                                    __html: subtitle 
                                }}
                            />
                        </Slide>
                    </Box>
                </Slide>
            </Card>
        </CardContainer>
    );
};

export default DrinkBaselineIntro;
